import { useEffect, useState, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cache to store hero images and reduce API calls
const heroImageCache = {};

/**
 * Custom hook to fetch hero image for a specific page
 * @param {string} pageName - The page identifier (e.g., 'home', 'podcasts')
 * @param {string} fallbackImage - Optional fallback image URL
 * @returns {string|null} - The hero image URL from admin or fallback
 */
export function useHeroImage(pageName, fallbackImage = null) {
  const [heroImageUrl, setHeroImageUrl] = useState(() => {
    // Try to load from cache immediately
    return heroImageCache[pageName] || fallbackImage;
  });
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    const loadHeroImage = async () => {
      // If we have a cached value, use it
      if (heroImageCache[pageName]) {
        if (isMounted.current) {
          setHeroImageUrl(heroImageCache[pageName]);
        }
        return;
      }

      try {
        const response = await axios.get(`${API}/hero-images/${pageName}`);
        if (response.data && response.data.image_url) {
          // Check if it's a full URL or relative path
          const imageUrl = response.data.image_url.startsWith('http')
            ? response.data.image_url
            : `${BACKEND_URL}${response.data.image_url}`;
          
          // Cache the result
          heroImageCache[pageName] = imageUrl;
          
          if (isMounted.current) {
            setHeroImageUrl(imageUrl);
          }
        } else if (fallbackImage && isMounted.current) {
          setHeroImageUrl(fallbackImage);
        }
      } catch (error) {
        // No hero image set - use fallback
        if (fallbackImage && isMounted.current) {
          setHeroImageUrl(fallbackImage);
        }
      }
    };

    loadHeroImage();

    return () => {
      isMounted.current = false;
    };
  }, [pageName, fallbackImage]);

  return heroImageUrl;
}

// Function to clear cache when admin updates images
export function clearHeroImageCache(pageName = null) {
  if (pageName) {
    delete heroImageCache[pageName];
  } else {
    // Clear all cache
    Object.keys(heroImageCache).forEach(key => delete heroImageCache[key]);
  }
}
